import { legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { selectAccessGrant, selectAccessState, selectSectionPaging } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { RootSubsFeed } from '@/deskUiSkeletons/subs/SubsFeed'
import { MoreLink } from '@/deskUiSkeletons/Shared/Shared'
import { PagingBar } from '@/msgComponents/controls/PagingBar/Modern/PagingBar'
import { RefreshSubs } from '@/msgComponents/controls/RefreshSubs/Modern/RefreshSubs'
import { CheckedsInfo } from '@/msgComponents/controls/CheckedsInfo/Modern/CheckedsInfo'
import { MailboxIcon } from './MailboxIcons'

function MailboxSection({ section }: { section: 0 | 1 }) {
  const { state } = useSubsStore()
  const access = selectAccessState(state)
  const isMessages = section === 1
  const sectionKey = isMessages ? 'submsges' : 'answers'
  const feedMode = state.renderProjection.mode === sectionKey ? sectionKey : 'msg'
  // CHECKING = POSSESSION: the checked bar must stay MOUNTED (legacy checker
  // writes counters into it) but hides for non-possessors.
  const canCheckOrMove = selectAccessGrant(state).capabilities.can_check_or_move
  // PAGING VIEW-MODEL: one live selector; no local page math (doc 24 §9).
  const { total, shownThrough, currentPage, totalPages, moreCount } = selectSectionPaging(state, isMessages)
  const panelContract: LegacyContractName = isMessages ? 'subs.submsges.front' : 'subs.answers.front'
  const listContract: LegacyContractName = isMessages ? 'subs.submsges.rootList' : 'subs.answers.rootList'

  return (
    <section {...legacyHookAttrs(panelContract, `md-mailbox-section min-h-0 bg-white ${isMessages ? 'compact' : 'comments'}`)} style={{ display: access.showUnlocked ? 'block' : 'none' }}>
      <header className="sticky top-0 z-[2] border-b border-[#dfe6ed] bg-[#fbfcfd] shadow-[0_1px_0_rgba(23,33,43,0.03)]">
        <div className="flex min-h-12 items-center gap-3 px-3">
          <div className={`flex h-8 w-8 flex-none items-center justify-center rounded-md ${isMessages ? 'bg-[#eef4ff] text-[#1556a8]' : 'bg-[#eaf6ef] text-[#27633a]'}`}>
            <MailboxIcon name={isMessages ? 'message' : 'inbox'} className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-[#24313d]">{isMessages ? 'Messages' : 'Inbox'}</div>
            <div className="text-[10px] text-[#75818c]">{shownThrough} of {total} items</div>
          </div>
          <RefreshSubs section={section} />
        </div>
        <div className="flex min-h-10 items-center justify-between gap-3 border-t border-[#edf1f4] bg-white px-3 py-1.5">
          <div className="min-w-0 flex-1">
            {canCheckOrMove
              ? <CheckedsInfo section={section} />
              : <span aria-hidden="true" style={{ display: 'none' }}><CheckedsInfo section={section} /></span>}
          </div>
          <PagingBar section={section} currentPage={currentPage} totalPages={totalPages} />
        </div>
      </header>
      <ul {...legacyHookAttrs(listContract, `page1 md-mailbox-list m-0 flex list-none flex-col p-0 ${isMessages ? 'md-subs-list--submsges' : 'md-subs-list--answers'}`)}>
        <RootSubsFeed mode={feedMode} activeModes={['msg', 'subs', sectionKey]} isSubmsges={isMessages} variant="hotmail" />
      </ul>
      <div className="px-3 py-2"><MoreLink section={section} moreCount={moreCount} /></div>
    </section>
  )
}

export function MailboxPanels({ activeSection }: { activeSection?: 0 | 1 }) {
  return (
    <div className="md-mailbox-panels min-h-0 flex-1 overflow-y-auto bg-white">
      {activeSection === undefined || activeSection === 0 ? <MailboxSection section={0} /> : null}
      {activeSection === undefined || activeSection === 1 ? <MailboxSection section={1} /> : null}
    </div>
  )
}
